/**
 * Asset map built from public/assets/manifest.json, the single source of art
 * paths. Nothing else hard-codes art filenames: every lookup goes through
 * this map, and every consumer has a CSS or inline-SVG fallback, so a missing
 * file never shows a broken image.
 *
 * The matcher is deliberately forgiving about the manifest's shape: entries
 * may sit in `assets` / `files` / `entries` / `items`, a bare array, or a
 * keyed object, and are recognized by `kind`, `value`, `world`, `name`,
 * `purpose` or the file path.
 */
import { useEffect, useState } from 'react';
import { manifestCardNames, setCardNames } from './brand';

export interface ManifestEntry {
  path: string;
  purpose?: string;
  kind?: string;
  width?: number;
  height?: number;
  format?: string;
  value?: number;
  name?: string;
  id?: string;
  key?: string;
  world?: number;
  density?: number;
  variant?: string;
}

export interface Img {
  src: string;
  /** Optional 2x variant for srcset. */
  src2x?: string;
  width?: number;
  height?: number;
}

export interface WorldArt {
  /** Landscape travel illustration (cover-fit). */
  travel?: Img;
  /** Responsive srcset (w descriptors) for the landscape illustration. */
  travelSet?: string;
  /** Portrait (4:5) crop for phones, when shipped. */
  travelPhone?: Img;
  medallion?: Img;
}

export interface AssetMap {
  loaded: boolean;
  /** Illustrations for the art cards (−5, 0..3, 10..12), keyed by value. */
  cardArt: Map<number, Img>;
  /** Primary crest lockup (shield + Tibbs + laurels + scroll). */
  crest?: Img;
  /** Crest with the FESTINA LENTE motto (About). */
  crestMotto?: Img;
  /** Horizontal lockup (shield + ANCIENT NINE) for the top bar on dark grounds. */
  lockup?: Img;
  /** Crest badge (shield, clubs, balls, Tibbs peeking over the top), and its version for lapis grounds (card back). */
  badge?: Img;
  badgeLapis?: Img;
  /** Shield-only crest (older manifests). */
  shield?: Img;
  /** One-color mono mark. */
  mark?: Img;
  hero?: Img;
  heroSet?: string;
  /** Portrait (4:5) crop of the hero for phones. */
  heroPortrait?: Img;
  worlds: Map<number, WorldArt>;
  trophyWin?: Img;
  trophyOther?: Img;
  amphora?: Img;
  stickers: Map<string, Img>;
  coach?: Img;
  /** Tibbs poses for hole-end reactions, when the art agent ships them separately. */
  tibbsCelebrate?: Img;
  tibbsRetract?: Img;
  icon?: Img;
}

const EMPTY: AssetMap = { loaded: false, cardArt: new Map(), worlds: new Map(), stickers: new Map() };

function url(path: string): string {
  if (/^https?:|^\//.test(path)) return path;
  return `/assets/${path.replace(/^(public\/)?assets\//, '')}`;
}

function entriesOf(raw: unknown): ManifestEntry[] {
  const isEntry = (e: unknown): e is ManifestEntry => !!e && typeof e === 'object' && typeof (e as ManifestEntry).path === 'string';
  const out: ManifestEntry[] = [];
  const visit = (v: unknown, keyHint: string | undefined, depth: number) => {
    if (depth > 4 || v === null || v === undefined) return;
    if (typeof v === 'string') {
      if (keyHint && /\.(webp|png|svg|jpe?g|avif)$/i.test(v)) out.push({ path: v, key: keyHint });
      return;
    }
    if (Array.isArray(v)) {
      v.forEach((x) => visit(x, keyHint, depth + 1));
      return;
    }
    if (typeof v !== 'object') return;
    if (isEntry(v)) {
      out.push(keyHint && !v.key ? { ...v, key: keyHint } : v);
      // Entries may carry their own variants (e.g. { path, x2: "...@2x.webp" }).
      for (const [k, x] of Object.entries(v)) {
        if (k !== 'path' && typeof x === 'string' && /\.(webp|png|svg|jpe?g|avif)$/i.test(x)) {
          out.push({ ...v, path: x, ...(/2x/i.test(k) || /@2x\./.test(x) ? { density: 2 } : {}) });
        }
      }
      return;
    }
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
      visit(x, keyHint ? `${keyHint}.${k}` : k, depth + 1);
    }
  };
  visit(raw, undefined, 0);
  return out;
}

const isPortrait = (e: ManifestEntry) =>
  /phone|portrait/i.test(e.path) || (typeof e.width === 'number' && typeof e.height === 'number' && e.height > e.width);
const isRetina = (e: ManifestEntry) => (e.density ?? 1) >= 2 || /@2x\./.test(e.path);
const pref = (e: ManifestEntry) => (/\.svg$/i.test(e.path) ? 0 : /\.webp$/i.test(e.path) ? 1 : /\.png$/i.test(e.path) ? 2 : 3);

/** Group 1x and 2x files into one Img (vector first, then WebP, then PNG). */
export function bestImg(list: ManifestEntry[]): Img | undefined {
  if (!list.length) return undefined;
  const sorted = [...list].sort((a, b) => pref(a) - pref(b) || (a.width ?? 0) - (b.width ?? 0));
  const one = sorted.find((e) => !isRetina(e)) ?? sorted[0]!;
  const two = sorted.find((e) => e !== one && isRetina(e) && pref(e) === pref(one));
  return {
    src: url(one.path),
    ...(two ? { src2x: url(two.path) } : {}),
    ...(one.width ? { width: one.width } : {}),
    ...(one.height ? { height: one.height } : {}),
  };
}

function wSet(list: ManifestEntry[]): string | undefined {
  const parts = list
    .filter((e) => /\.(webp|png|jpe?g|avif)$/i.test(e.path) && typeof e.width === 'number')
    .sort((a, b) => (a.width ?? 0) - (b.width ?? 0))
    .filter((e, i, arr) => i === 0 || arr[i - 1]!.width !== e.width)
    .map((e) => `${url(e.path)} ${e.width}w`);
  return parts.length > 1 ? parts.join(', ') : undefined;
}


export function buildAssetMap(raw: unknown): AssetMap {
  const all = entriesOf(raw);
  const map: AssetMap = { loaded: true, cardArt: new Map(), worlds: new Map(), stickers: new Map() };
  const text = (e: ManifestEntry) => `${e.kind ?? ''} ${e.key ?? ''} ${e.id ?? ''} ${e.name ?? ''} ${e.purpose ?? ''} ${e.path}`.toLowerCase();
  const kindOrPath = (e: ManifestEntry) => `${e.kind ?? ''} ${e.key ?? ''} ${e.id ?? ''} ${e.path}`.toLowerCase();
  const used = new Set<ManifestEntry>();
  const take = (list: ManifestEntry[]) => {
    list.forEach((e) => used.add(e));
    return list;
  };

  // ---- card art: anything card-ish with a value (or a value in its file name) that isn't the back
  const faces = new Map<number, ManifestEntry[]>();
  for (const e of all) {
    const t = kindOrPath(e);
    if (!/card|face/.test(t) || /back/.test(t)) continue;
    let v = typeof e.value === 'number' ? e.value : null;
    if (v === null) {
      const m = /(?:face|card|art)[-_]?(m|minus|neg|-)?(\d{1,2})(?![\d])/.exec(e.path.toLowerCase());
      if (m) v = m[1] ? -Number(m[2]) : Number(m[2]);
    }
    if (v === null || v < -5 || v > 12) continue;
    if (!faces.has(v)) faces.set(v, []);
    faces.get(v)!.push(e);
  }
  for (const [v, list] of faces) {
    const img = bestImg(take(list));
    if (img) map.cardArt.set(v, img);
  }

  const find = (re: RegExp, exclude?: RegExp, src: (e: ManifestEntry) => string = kindOrPath) =>
    all.filter((e) => !used.has(e) && re.test(src(e)) && !(exclude && exclude.test(src(e))));

  // ---- worlds: travel illustrations and medallions, by `world` number or a 1..9 in the name
  const worldNo = (e: ManifestEntry): number | null => {
    if (typeof e.world === 'number' && e.world >= 1 && e.world <= 9) return e.world;
    const m = /(?:world|travel|medallion|course|hole)[-_/]?0?([1-9])(?!\d)/.exec(kindOrPath(e));
    if (m) return Number(m[1]);
    return null;
  };
  const worldEntries = all.filter((e) => !used.has(e) && /world|travel|medallion|course/.test(kindOrPath(e)) && worldNo(e) !== null);
  for (let n = 1; n <= 9; n++) {
    const mine = worldEntries.filter((e) => worldNo(e) === n);
    if (!mine.length) continue;
    const medal = mine.filter((e) => /medal|icon|badge/.test(kindOrPath(e)));
    const travel = mine.filter((e) => !medal.includes(e));
    const phone = travel.filter(isPortrait);
    const land = travel.filter((e) => !phone.includes(e));
    take(mine);
    const art: WorldArt = {};
    const t = bestImg(land);
    if (t) art.travel = t;
    const ts = wSet(land);
    if (ts) art.travelSet = ts;
    const tp = bestImg(phone);
    if (tp) art.travelPhone = tp;
    const m = bestImg(medal);
    if (m) art.medallion = m;
    map.worlds.set(n, art);
  }

  // ---- stickers (reactions)
  const stickerKeys: [string, RegExp][] = [
    ['well-played', /well[-_ ]?played|applaud|clap/],
    ['oof', /oof|retract|dismay/],
    ['smug', /smug/],
    ['fore', /fore(?!st)/],
  ];
  const stickerish = all.filter((e) => !used.has(e) && /sticker|reaction|emote/.test(kindOrPath(e)));
  for (const [k, re] of stickerKeys) {
    const img = bestImg(take(stickerish.filter((e) => re.test(text(e)))));
    if (img) map.stickers.set(k, img);
  }

  // ---- trophy
  const trophies = find(/trophy|amphora/);
  const amph = trophies.filter((e) => /icon|alone|amphora(?![-_ ]?(win|other))/.test(kindOrPath(e)) && !/win|other|applaud/.test(kindOrPath(e)));
  map.amphora = bestImg(take(amph));
  map.trophyWin = bestImg(take(trophies.filter((e) => !used.has(e) && /win|won|celebrat/.test(kindOrPath(e)) && !/other/.test(kindOrPath(e)))));
  map.trophyOther = bestImg(take(trophies.filter((e) => !used.has(e) && /other|lose|lost|applaud/.test(kindOrPath(e)))));

  // ---- coach and reaction poses
  map.coach = bestImg(take(find(/coach/)));
  map.tibbsCelebrate = bestImg(take(find(/(tibbs|tom).*celebrat|celebrat.*(tibbs|tom)|pose[-_]?7/)));
  map.tibbsRetract = bestImg(take(find(/(tibbs|tom).*(retract|dismay)|(retract|dismay).*(tibbs|tom)|pose[-_]?8/)));

  // ---- crest lockups
  const crestish = find(/crest|logo|shield|wordmark|brand\//, /favicon|apple-touch|icon-\d|og-image|\bog\b|share/);
  const pick = (re: RegExp) => crestish.filter((e) => !used.has(e) && re.test(e.path.toLowerCase()));
  map.lockup = bestImg(take(pick(/horizontal|lockup-h/)));
  map.badgeLapis = bestImg(take(pick(/badge.*lapis|lapis.*badge/)));
  map.badge = bestImg(take(pick(/badge/)));
  map.crestMotto = bestImg(take(pick(/motto/)));
  map.shield = bestImg(take(pick(/shield/)));
  map.mark = bestImg(take(pick(/mono|mark/)));
  const primary = crestish.filter((e) => !used.has(e) && /crest|logo/.test(kindOrPath(e)));
  map.crest = bestImg(take(primary));

  // ---- home hero (landscape, plus a portrait crop for phones)
  const hero = find(/hero|splash|home/, /icon/);
  const heroRaster = hero.filter((e) => !/\.svg$/i.test(e.path));
  const heroPortrait = heroRaster.filter(isPortrait);
  const heroLand = heroRaster.filter((e) => !heroPortrait.includes(e));
  const heroSmall = heroLand.filter((e) => (e.width ?? 9999) <= 1400);
  map.hero = bestImg(take(heroSmall.length ? heroSmall : heroLand));
  map.heroSet = wSet(heroLand);
  map.heroPortrait = bestImg(take(heroPortrait));
  map.icon = bestImg(find(/icon-192|app-icon|icon/, /favicon|medal|sticker/));
  return map;
}

let current: AssetMap = EMPTY;
let pending: Promise<AssetMap> | null = null;
const subs = new Set<(m: AssetMap) => void>();

export function loadAssets(): Promise<AssetMap> {
  if (pending) return pending;
  pending = fetch('/assets/manifest.json', { cache: 'no-cache' })
    .then((r) => (r.ok ? r.json() : null))
    .then((raw) => {
      setCardNames(manifestCardNames(raw));
      return buildAssetMap(raw);
    })
    .catch(() => ({ ...EMPTY, loaded: true }))
    .then((m) => {
      current = m;
      subs.forEach((fn) => fn(m));
      return m;
    });
  return pending;
}

export function useAssets(): AssetMap {
  const [m, setM] = useState<AssetMap>(current);
  useEffect(() => {
    subs.add(setM);
    void loadAssets();
    if (current !== m) setM(current);
    return () => {
      subs.delete(setM);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return m;
}

export function srcSet(img: Img | undefined): string | undefined {
  if (!img?.src2x) return undefined;
  return `${img.src} 1x, ${img.src2x} 2x`;
}
